import React, { useState, useEffect } from 'react';
import { api } from './api/api';

const RATE_LABELS = { per_kg: 'Per KG', per_ton: 'Per Ton', per_awb: 'Per AWB', free: 'Free' };
const TYPE_BADGES = { GCR: 'badge-gcr', AVI: 'badge-avi', VAL: 'badge-val', DG: 'badge-dg', MAIL: 'badge-mail', PER: 'badge-per' };

const ServicesScreen = () => {
  const [services, setServices] = useState([]);
  const [packingItems, setPackingItems] = useState([]);
  const [buildUpItems, setBuildUpItems] = useState([]);
  const [storageItems, setStorageItems] = useState([]);
  const [cargoTypes, setCargoTypes] = useState([]);
  const [freePeriodItems, setFreePeriodItems] = useState([]);
  const [activeTab, setActiveTab] = useState('services');
  const [exchangeRate, setExchangeRate] = useState(2500);
  const [savingRate, setSavingRate] = useState(false);
  const [rateMsg, setRateMsg] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalKind, setModalKind] = useState('services');
  const [modalMode, setModalMode] = useState('add');
  const [form, setForm] = useState({
    id: null,
    type: '',
    classification: '',
    rateType: 'per_kg',
    rangeFrom: '',
    rangeTo: '',
    prices: {},
    name: '',
    isFixed: false,
    freePeriod: ''
  });
  const [calcType, setCalcType] = useState('');
  const [calcSelectedType, setCalcSelectedType] = useState('');
  const [calcWeight, setCalcWeight] = useState(150);
  const [calcAwb, setCalcAwb] = useState(1);
  const [calcOut, setCalcOut] = useState('— USD');
  const [calcBreakdown, setCalcBreakdown] = useState('');
  const [storageCalcType, setStorageCalcType] = useState('GCR');
  const [storageCalcWeight, setStorageCalcWeight] = useState(150);
  const [storageCalcDays, setStorageCalcDays] = useState(1);
  const [storageCalcOut, setStorageCalcOut] = useState('— USD');
  const [storageCalcBreakdown, setStorageCalcBreakdown] = useState('');
  const [newCargoType, setNewCargoType] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([fetchServices(), fetchPacking(), fetchBuildUp(), fetchStorage(), fetchCargoTypes(), fetchFreePeriods(), fetchExchangeRate()]);
  };

  const fetchExchangeRate = async () => {
    try {
      const res = await api.get('/services/exchange-rate');
      if (res && res.rate) {
        setExchangeRate(res.rate);
      }
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
    }
  };

  const handleSaveExchangeRate = async () => {
    try {
      setSavingRate(true);
      setRateMsg('');
      const res = await api.post('/services/exchange-rate', { rate: exchangeRate });
      if (res && res.rate) {
        setExchangeRate(res.rate);
        setRateMsg('✅ Exchange rate saved successfully');
      }
    } catch (err) {
      console.error('Error saving exchange rate:', err);
      setRateMsg('❌ Error saving exchange rate');
    } finally {
      setSavingRate(false);
    }
  };

  const fetchServices = async () => {
    try {
      const res = await api.get('/services');
      if (Array.isArray(res)) {
        const mapped = res.map(s => {
          // تحويل الأسعار من s.prices وتصغير الحروف لتطابق مسميات الأعمدة في الجدول
          const flattenedPrices = {};
          if (s.prices) {
            Object.keys(s.prices).forEach(key => {
              flattenedPrices[key.toLowerCase()] = s.prices[key];
            });
          }

          return {
            ...s,
            id: s.id,
            type: s.type,
            classification: s.classification,
            rateType: s.rate_type,
            rangeFrom: s.range_from ?? null,
            rangeTo: s.range_to ?? null,
            ...flattenedPrices // دمج الأسعار (gcr, avi...) في الكائن الأساسي
          };
        });
        setServices(mapped);
        if (!calcSelectedType && mapped.length > 0) {
          setCalcSelectedType([...new Set(mapped.map((s) => s.type))][0]);
        }
      }
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  };

  const fetchPacking = async () => {
    try {
      const res = await api.get('/packing');
      if (Array.isArray(res)) {
        setPackingItems(res);
      }
    } catch (err) {
      console.error('Error fetching packing items:', err);
    }
  };

  const fetchBuildUp = async () => {
    try {
      const res = await api.get('/build-up');
      if (Array.isArray(res)) {
        setBuildUpItems(res);
      }
    } catch (err) {
      console.error('Error fetching build up items:', err);
    }
  };

  const fetchStorage = async () => {
    try {
      const res = await api.get('/storage');
      if (Array.isArray(res)) {
        const mapped = res.map((item) => ({
          ...item,
          rangeFrom: item.range_from ?? item.rangeFrom ?? null,
          rangeTo: item.range_to ?? item.rangeTo ?? null,
          isFixed: item.is_fixed ?? item.isFixed ?? false
        }));
        setStorageItems(mapped);
      }
    } catch (err) {
      console.error('Error fetching storage items:', err);
    }
  };

  const fetchCargoTypes = async () => {
    try {
      const res = await api.get('/cargo-types');
      if (Array.isArray(res)) {
        setCargoTypes(res);
        if (!calcType && res.length > 0) {
          setCalcType(res[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching cargo types:', err);
    }
  };

  const fetchFreePeriods = async () => {
    try {
      const res = await api.get('/free-periods');
      if (Array.isArray(res)) {
        setFreePeriodItems(res);
      }
    } catch (err) {
      console.error('Error fetching free periods:', err);
    }
  };

  const rateTypeBadge = (rt) => {
    const map = { per_kg: 'rt-kg', per_ton: 'rt-ton', per_awb: 'rt-awb', free: 'rt-free' };
    return <span className={`rate-type ${map[rt] || 'rt-free'}`}>{RATE_LABELS[rt] || rt}</span>;
  };

  const fmtVal = (v) => {
    if (v === 'Free' || v === 'No need') return <span style={{ color: 'var(--color-text-secondary)' }}>{v}</span>;
    return typeof v === 'number' ? `$${v}` : v;
  };

  const formatScope = (s) => {
    if (!s) return '';
    if (s.rateType === 'per_awb') {
      if (s.rangeFrom || s.rangeTo) {
        return `AWB ${s.rangeFrom ?? 1}-${s.rangeTo ?? '∞'}`;
      }
      return 'Per AWB';
    }
    if (s.rateType === 'per_ton') {
      if (s.rangeFrom || s.rangeTo) {
        return `${s.rangeFrom ?? 11000}-${s.rangeTo ?? '∞'} kg`;
      }
      return 'Per Ton';
    }
    if (s.rateType === 'per_kg') {
      if (s.rangeFrom != null || s.rangeTo != null) {
        return `${s.rangeFrom ?? 0}-${s.rangeTo ?? '∞'} kg`;
      }
      return s.classification || 'Per KG';
    }
    return s.classification || '';
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({
      id: null,
      type: '',
      classification: '',
      rateType: 'per_kg',
      rangeFrom: '',
      rangeTo: '',
      prices: {},
      name: '',
      isFixed: false
    });
  };

  const openModal = (kind, mode = 'add', item = null) => {
    setModalOpen(true);
    setModalKind(kind);
    setModalMode(mode);
    if (kind === 'services') {
      if (mode === 'edit' && item) {
        setForm({
          id: item.id,
          type: item.type,
          classification: item.classification,
          rateType: item.rateType,
          rangeFrom: item.rangeFrom ?? '',
          rangeTo: item.rangeTo ?? '',
          prices: cargoTypes.reduce((acc, ct) => {
            if (ct.name) {
              const key = ct.name.toLowerCase();
              acc[key] = item[key] ?? '';
            }
            return acc;
          }, {}),
          name: '',
        });
      } else {
        resetForm();
      }
    } else if (kind === 'packing') {
      if (mode === 'edit' && item) {
        setForm({
          id: item.id,
          type: item.type,
          classification: item.classification,
          rateType: 'per_kg',
          rangeFrom: '',
          rangeTo: '',
          gcr: '',
          avi: '',
          val: '',
          dg: '',
          mail: '',
          per: '',
          name: '',
          price: item.price ?? ''
        });
      } else {
        setForm({
          id: null,
          type: '',
          classification: '',
          rateType: 'per_kg',
          rangeFrom: '',
          rangeTo: '',
          gcr: '',
          avi: '',
          val: '',
          dg: '',
          mail: '',
          per: '',
          name: '',
          price: ''
        });
      }
    } else if (kind === 'build_up') {
      setForm({
        id: item?.id ?? null,
        type: '',
        classification: '',
        rateType: 'per_kg',
        rangeFrom: '',
        rangeTo: '',
        gcr: '',
        avi: '',
        val: '',
        dg: '',
        mail: '',
        per: '',
        name: item?.name ?? '',
        price: item?.price ?? '',
        isFixed: false
      });
    } else if (kind === 'storage') {
      if (mode === 'edit' && item) {
        setForm({
          id: item.id,
          type: item.type,
          classification: item.classification,
          rateType: 'per_kg',
          rangeFrom: item.rangeFrom ?? '',
          rangeTo: item.rangeTo ?? '',
          gcr: '',
          avi: '',
          val: '',
          dg: '',
          mail: '',
          per: '',
          name: '',
          price: item.price === 'No need' ? '' : item.price,
          isFixed: item.isFixed,
          freePeriod: ''
        });
      } else {
        resetForm();
      }
    } else if (kind === 'free_periods') {
      if (mode === 'edit' && item) {
        setForm({
          id: item.id,
          type: item.type,
          classification: '',
          rateType: 'per_kg',
          rangeFrom: '',
          rangeTo: '',
          gcr: '',
          avi: '',
          val: '',
          dg: '',
          mail: '',
          per: '',
          name: '',
          price: '',
          isFixed: false,
          freePeriod: item.free_period ?? item.freePeriod ?? ''
        });
      } else {
        setForm({
          id: null,
          type: '',
          classification: '',
          rateType: 'per_kg',
          rangeFrom: '',
          rangeTo: '',
          gcr: '',
          avi: '',
          val: '',
          dg: '',
          mail: '',
          per: '',
          name: '',
          price: '',
          isFixed: false,
          freePeriod: ''
        });
      }
    } else if (kind === 'cargo_types') {
      setForm({
        id: null,
        type: '',
        classification: '',
        rateType: 'per_kg',
        rangeFrom: '',
        rangeTo: '',
        gcr: '',
        avi: '',
        val: '',
        dg: '',
        mail: '',
        per: '',
        name: '',
        price: '',
        isFixed: false,
        freePeriod: ''
      });
    }
  };

  const parseVal = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : v;
  };

  const parseRange = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  const saveModal = async () => {
    try {
      if (modalKind === 'services') {
        const prices = {};
        cargoTypes.forEach(ct => {
          if (ct.name) {
            const key = ct.name.toLowerCase();
            prices[ct.name] = parseVal(form.prices[key]);
          }
        });

        const payload = {
          type: form.type || 'New',
          classification: form.classification || '—',
          rateType: form.rateType,
          rangeFrom: parseRange(form.rangeFrom),
          rangeTo: parseRange(form.rangeTo),
          prices
        };

        const mapService = (svc) => {
          const flattenedPrices = {};
          if (svc.prices) {
            Object.keys(svc.prices).forEach(key => {
              flattenedPrices[key.toLowerCase()] = svc.prices[key];
            });
          }
          return {
            ...svc,
            id: svc.id,
            rateType: svc.rate_type ?? svc.rateType,
            rangeFrom: svc.range_from ?? svc.rangeFrom ?? null,
            rangeTo: svc.range_to ?? svc.rangeTo ?? null,
            ...flattenedPrices
          };
        };

        if (modalMode === 'edit') {
          const updated = await api.put(`/services/${form.id}`, payload);
          setServices(services.map((s) => (s.id === form.id ? mapService(updated) : s)));
        } else {
          const saved = await api.post('/services', payload);
          setServices([...services, mapService(saved)]);
        }
      } else if (modalKind === 'packing') {
        const payload = {
          type: form.type || '—',
          classification: form.classification || '—',
          price: parseFloat(form.price)
        };
        if (!Number.isFinite(payload.price)) {
          alert('Price is required and must be a valid number');
          return;
        }
        if (modalMode === 'edit') {
          await api.put(`/packing/${form.id}`, payload);
          setPackingItems(packingItems.map((item) => (item.id === form.id ? { ...item, ...payload } : item)));
        } else {
          const saved = await api.post('/packing', payload);
          setPackingItems([...packingItems, saved]);
        }
      } else if (modalKind === 'build_up') {
        const payload = {
          name: form.name || '—',
          price: parseFloat(form.price)
        };
        if (!Number.isFinite(payload.price)) {
          alert('Price is required and must be a valid number');
          return;
        }
        if (modalMode === 'edit') {
          await api.put(`/build-up/${form.id}`, payload);
          setBuildUpItems(buildUpItems.map((item) => (item.id === form.id ? { ...item, ...payload } : item)));
        } else {
          const saved = await api.post('/build-up', payload);
          setBuildUpItems([...buildUpItems, saved]);
        }
      } else if (modalKind === 'storage') {
        const price = form.price === '' ? 'No need' : parseFloat(form.price);
        if (form.price !== '' && !Number.isFinite(price)) {
          alert('Price must be a valid number or empty for No need');
          return;
        }
        const payload = {
          type: form.type,
          classification: form.classification,
          rangeFrom: parseRange(form.rangeFrom),
          rangeTo: parseRange(form.rangeTo),
          price: price,
          isFixed: form.isFixed
        };
        try {
          if (modalMode === 'edit') {
            await api.put(`/storage/${form.id}`, payload);
            setStorageItems(storageItems.map((item) => (item.id === form.id ? { ...item, ...payload } : item)));
          } else {
            const saved = await api.post('/storage', payload);
            setStorageItems([...storageItems, saved]);
          }
        } catch (err) {
          console.error('Error saving storage item:', err);
        }
      } else if (modalKind === 'free_periods') {
        if (!form.type) {
          alert('Cargo Type is required');
          return;
        }
        const payload = {
          type: form.type,
          freePeriod: form.freePeriod || 'None'
        };
        try {
          if (modalMode === 'edit') {
            await api.put(`/free-periods/${form.id}`, payload);
            setFreePeriodItems(freePeriodItems.map((item) => (item.id === form.id ? { ...item, ...payload } : item)));
            alert('Free Period updated successfully!');
          } else {
            const saved = await api.post('/free-periods', payload);
            setFreePeriodItems([...freePeriodItems, saved]);
            alert('Free Period added successfully!');
          }
        } catch (err) {
          console.error('Error saving free period item:', err);
        }
      }
      closeModal();
      loadData(); // Reload all data to ensure consistency
    } catch (err) {
      console.error('Error saving item:', err);
      alert('Error saving item: ' + (err.message || 'Unknown error'));
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMode('add');
    setModalKind('services');
    resetForm();
  };

  const deleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await api.del(`/services/${id}`);
      setServices(services.filter((s) => s.id !== id));
    } catch (err) {
      console.error('Error deleting service:', err);
    }
  };

  const deletePackingItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this packing item?')) return;
    try {
      await api.del(`/packing/${id}`);
      setPackingItems(packingItems.filter((item) => item.id !== id));
    } catch (err) {
      console.error('Error deleting packing item:', err);
    }
  };

  const deleteBuildUpItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this build up item?')) return;
    try {
      await api.del(`/build-up/${id}`);
      setBuildUpItems(buildUpItems.filter((item) => item.id !== id));
    } catch (err) {
      console.error('Error deleting build up item:', err);
    }
  };

  const deleteStorageItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this storage item?')) return;
    try {
      await api.del(`/storage/${id}`);
      setStorageItems(storageItems.filter((item) => item.id !== id));
    } catch (err) {
      console.error('Error deleting storage item:', err);
    }
  };

  const deleteFreePeriodItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this free period item?')) return;
    try {
      await api.del(`/free-periods/${id}`);
      setFreePeriodItems(freePeriodItems.filter((item) => item.id !== id));
    } catch (err) {
      console.error('Error deleting free period item:', err);
    }
  };

  const addCargoType = async () => {
    if (newCargoType.trim() && !cargoTypes.find(ct => ct.name === newCargoType.trim())) {
      try {
        const saved = await api.post('/cargo-types', { name: newCargoType.trim() });
        setCargoTypes([...cargoTypes, saved]);
        setNewCargoType('');
      } catch (err) {
        console.error('Error adding cargo type:', err);
      }
    }
  };

  const deleteCargoType = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete cargo type "${name}"?`)) return;
    try {
      await api.del(`/cargo-types/${name}`); // Changed from id to name
      setCargoTypes(cargoTypes.filter(t => t.id !== id));
      setStorageItems(storageItems.filter(item => item.cargo_type_id !== id));
    } catch (err) {
      console.error('Error deleting cargo type:', err);
    }
  };

  const renderServiceTable = () => (
    <table className="grid-table">
      <thead>
        <tr>
          <th>Service Group (Type)</th>
          <th>Classification</th>
          <th>Rate Type</th>
          {cargoTypes.filter(ct => ct && ct.name).map(ct => (
            <th key={ct.id}>{ct.name} (USD)</th>
          ))}
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {services.map((s) => (
          <tr key={s.id}>
            <td>{s.type}</td>
            <td style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>{formatScope(s)}</td>
            <td>{rateTypeBadge(s.rateType)}</td>
            {cargoTypes.filter(ct => ct && ct.name).map(ct => (
              <td key={ct.id}><strong>{fmtVal(s[ct.name.toLowerCase()])}</strong></td>
            ))}
            <td>
              <div className="action-btns">
                <button className="btn" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => openModal('services', 'edit', s)}>Edit</button>
                <button className="btn btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => deleteService(s.id)}>Delete</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderPackingTable = () => (
    <table className="grid-table">
      <thead>
        <tr>
          <th>Packing Type</th>
          <th>Classification</th>
          <th>Price (USD)</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {packingItems.map((item) => (
          <tr key={item.id}>
            <td>{item.type}</td>
            <td>{item.classification}</td>
            <td>{item.price}</td>
            <td>
              <div className="action-btns">
                <button className="btn" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => openModal('packing', 'edit', item)}>Edit</button>
                <button className="btn btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => deletePackingItem(item.id)}>Delete</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderBuildUpTable = () => (
    <table className="grid-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Price (USD)</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {buildUpItems.map((item) => (
          <tr key={item.id}>
            <td>{item.name}</td>
            <td>{item.price}</td>
            <td>
              <div className="action-btns">
                <button className="btn" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => openModal('build_up', 'edit', item)}>Edit</button>
                <button className="btn btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => deleteBuildUpItem(item.id)}>Delete</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderStorageTable = () => (
    <table className="grid-table">
      <thead>
        <tr>
          <th>Cargo Type</th>
          <th>Classification</th>
          <th>Range</th>
          <th>Price</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {storageItems.map((r) => (
          <tr key={r.id}>
            <td>{r.type}</td>
            <td>{r.classification}</td>
            <td>{r.rangeFrom}-{r.rangeTo} kg</td>
            <td>{r.price === 'No need' ? 'No need' : `$${r.price} ${r.isFixed ? '' : 'per kg'}`}</td>
            <td>
              <div className="action-btns">
                <button className="btn" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => openModal('storage', 'edit', r)}>Edit</button>
                <button className="btn btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => deleteStorageItem(r.id)}>Delete</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderStorageCalc = () => (
    <div className="calc-panel">
      <h3>Storage Cost Calculator</h3>
      <div className="calc-grid">
        <div className="calc-field">
          <label>Cargo Type</label>
          <select className="edit-select" value={storageCalcType} onChange={(e) => setStorageCalcType(e.target.value)}>
            {cargoTypes.filter(ct => ct && ct.name).map(ct => (
              <option key={ct.id} value={ct.id}>{ct.name}</option>
            ))}
          </select>
        </div>
        <div className="calc-field">
          <label>Weight (KG)</label>
          <input type="number" className="edit-input" value={storageCalcWeight} min="1" onChange={(e) => setStorageCalcWeight(e.target.value)} />
        </div>
        <div className="calc-field">
          <label>Days</label>
          <input type="number" className="edit-input" value={storageCalcDays} min="1" onChange={(e) => setStorageCalcDays(e.target.value)} />
        </div>
      </div>
      <div className="calc-result">
        <div>
          <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Total Storage Cost</div>
          <div className="calc-total">{storageCalcOut}</div>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', textAlign: 'right', lineHeight: '1.6' }}>{storageCalcBreakdown}</div>
      </div>
    </div>
  );

  const renderStorageSection = () => (
    <div>
      <h3>CARGO STORAGE PERIOD FOR EXPORT (PER DAYS) USD</h3>
      <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
        {renderStorageTable()}
      </div>
      {renderStorageCalc()}
    </div>
  );

  const renderFreePeriodTable = () => (
    <table className="grid-table">
      <thead>
        <tr>
          <th>Cargo Type</th>
          <th>Free Period</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {freePeriodItems.map((item) => (
          <tr key={item.id}>
            <td>{item.type}</td>
            <td>{item.free_period || item.freePeriod || 'None'}</td>
            <td>
              <div className="action-btns">
                <button className="btn" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => openModal('free_periods', 'edit', item)}>Edit</button>
                <button className="btn btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => deleteFreePeriodItem(item.id)}>Delete</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderFreePeriodSection = () => (
    <div>
      <h3>Free Storage Period for Export</h3>
      <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
        {renderFreePeriodTable()}
      </div>
    </div>
  );

  const findRow = (weight, type) => {
    const matchingType = services.filter((s) => s.type === type);
    if (matchingType.length === 0) return null;

    const weightServices = matchingType.filter((s) => s.rateType === 'per_kg' || s.rateType === 'per_ton');
    for (const s of weightServices) {
      const from = Number(s.rangeFrom ?? 0);
      const toParsed = Number(s.rangeTo);
      const to = Number.isFinite(toParsed) ? toParsed : Infinity;
      if (weight >= from && (to === null || weight <= to)) {
        return s;
      }
    }

    const defaultService = matchingType.find((s) => s.rateType === 'per_kg' || s.rateType === 'per_ton');
    if (defaultService) return defaultService;

    return matchingType[0];
  };

  const getRateTypeForType = (type) => {
    const service = services.find((s) => s.type === type);
    return service ? service.rateType : 'per_kg';
  };

  const calcCost = () => {
    const weight = parseFloat(calcWeight) || 0;
    const awbCount = parseFloat(calcAwb) || 0;
    const selectedType = calcSelectedType;
    const cargoTypeId = calcType; // نستخدم calcType كـ ID لنوع البضاعة

    // البحث عن الخدمة المطابقة للمجموعة والوزن
    const rowCandidates = services
      .filter((s) => s.type === selectedType)
      .map((s) => {
        const fromRaw = s.rangeFrom ?? s.range_from;
        const toRaw = s.rangeTo ?? s.range_to;
        const from = Number(fromRaw);
        const to = Number(toRaw);
        return {
          ...s,
          _from: Number.isFinite(from) ? from : 0,
          _to: Number.isFinite(to) ? to : Infinity,
          _hasRange: fromRaw != null || toRaw != null,
        };
      })
      .sort((a, b) => {
        if (a._hasRange !== b._hasRange) return a._hasRange ? -1 : 1;
        if (a._from !== b._from) return a._from - b._from;
        return a._to - b._to;
      });

    const row = rowCandidates.find((s) => weight >= s._from && weight <= s._to) || rowCandidates[0];

    if (!row) {
      setCalcOut('— USD');
      setCalcBreakdown('No suitable classification for this weight/type');
      return;
    }

    const cargoTypeName = cargoTypes.find(ct => ct.id == cargoTypeId)?.name;
    const priceKey = cargoTypeName ? cargoTypeName.toLowerCase() : 'gcr';
    const rate = row[priceKey];
    if (rate === 'Free' || rate === 'No need') {
      setCalcOut('Free');
      setCalcBreakdown('This service is free for this classification');
      return;
    }

    let total = 0;
    let breakdown = '';

    if (row.rateType === 'per_kg') {
      total = rate * weight;
      breakdown = `$${rate} × ${weight} kg = $${total.toFixed(2)}`;
    } else if (row.rateType === 'per_ton') {
      const tons = weight / 1000;
      total = rate * tons;
      breakdown = `$${rate}/ton × ${tons.toFixed(2)} ton = $${total.toFixed(2)}`;
    } else if (row.rateType === 'per_awb') {
      total = rate * awbCount;
      breakdown = `$${rate}/AWB × ${awbCount} AWB = $${total.toFixed(2)}`;
    }

    setCalcOut(`$${total.toFixed(2)} USD`);
    setCalcBreakdown(breakdown);
  };

  useEffect(() => {
    if (activeTab === 'calc') {
      calcCost();
    }
  }, [calcType, calcSelectedType, calcWeight, calcAwb, services, activeTab]);

  useEffect(() => {
    if (!calcSelectedType && services.length > 0) {
      setCalcSelectedType([...new Set(services.map((s) => s.type))][0]);
    }
  }, [services, calcSelectedType]);

  const calcStorageCost = () => {
    const weight = parseFloat(storageCalcWeight) || 0;
    const days = parseFloat(storageCalcDays) || 0;
    const type = storageCalcType;
    const rates = storageItems.filter(r => r.type === type).sort((a, b) => a.rangeFrom - b.rangeFrom);
    if (rates.length === 0) {
      setStorageCalcOut('— USD');
      setStorageCalcBreakdown('No rates found for this type');
      return;
    }
    let dailyCost = 0;
    let breakdownParts = [];
    let remainingWeight = weight;
    for (const rate of rates) {
      if (remainingWeight <= 0) break;
      const rangeStart = rate.rangeFrom;
      const rangeEnd = rate.rangeTo || Infinity;
      const weightInRange = Math.min(remainingWeight, rangeEnd - rangeStart + 1); // assuming inclusive
      if (weightInRange > 0) {
        if (rate.price === 'No need') {
          dailyCost = 0;
          breakdownParts = ['No need'];
          break;
        }
        if (rate.isFixed) {
          dailyCost += rate.price;
          breakdownParts.push(`$${rate.price} (${rate.classification})`);
        } else {
          const costInRange = rate.price * weightInRange;
          dailyCost += costInRange;
          breakdownParts.push(`$${rate.price} × ${weightInRange}kg (${rate.classification})`);
        }
        remainingWeight -= weightInRange;
      }
    }
    if (dailyCost === 0 && breakdownParts.includes('No need')) {
      setStorageCalcOut('No need');
      setStorageCalcBreakdown('Storage not required for this cargo type');
      return;
    }
    const total = dailyCost * days;
    const breakdown = breakdownParts.join(' + ') + ` × ${days} days = $${total.toFixed(2)}`;
    setStorageCalcOut(`$${total.toFixed(2)} USD`);
    setStorageCalcBreakdown(breakdown);
  };

  useEffect(() => {
    if (activeTab === 'storage') {
      calcStorageCost();
    }
  }, [storageCalcType, storageCalcWeight, storageCalcDays, activeTab]);

  const currentLabel = activeTab === 'services'
    ? 'Service'
    : activeTab === 'packing'
      ? 'Packing'
      : activeTab === 'build_up'
        ? 'Build Up'
        : activeTab === 'storage'
          ? 'Storage'
          : activeTab === 'free_periods'
            ? 'Free Period'
            : 'Service';

  return (
    <div className="page">
      <div className="top-bar">
        <h2>Manage Services — Export Air Cargo</h2>
        {activeTab !== 'calc' && (
          <div>
            <button className="btn btn-primary" onClick={() => openModal(activeTab)}>
              + Add {currentLabel}
            </button>
            {(activeTab === 'storage' || activeTab === 'free_periods') && (
              <button className="btn btn-secondary" style={{ marginLeft: '10px' }} onClick={() => openModal('cargo_types')}>
                Manage Types
              </button>
            )}
          </div>
        )}
      </div>

      <div className="tabs">
        <div className={`tab ${activeTab === 'services' ? 'active' : ''}`} onClick={() => setActiveTab('services')}>
          Services Table
        </div>
        <div className={`tab ${activeTab === 'packing' ? 'active' : ''}`} onClick={() => setActiveTab('packing')}>
          Packing
        </div>
        <div className={`tab ${activeTab === 'build_up' ? 'active' : ''}`} onClick={() => setActiveTab('build_up')}>
          Build Up
        </div>
        <div className={`tab ${activeTab === 'storage' ? 'active' : ''}`} onClick={() => setActiveTab('storage')}>
          Cargo Storage
        </div>
        <div className={`tab ${activeTab === 'free_periods' ? 'active' : ''}`} onClick={() => setActiveTab('free_periods')}>
          Free Period
        </div>
        <div className={`tab ${activeTab === 'exchange_rate' ? 'active' : ''}`} onClick={() => setActiveTab('exchange_rate')}>
          Exchange Rate
        </div>
      {/* <div className={`tab ${activeTab === 'calc' ? 'active' : ''}`} onClick={() => setActiveTab('calc')}>
          Cost Calculator
        </div> */}
      </div>

      <div className="section-wrap">
        {activeTab === 'services' && (
          <div style={{ overflowX: 'auto' }}>
            {renderServiceTable()}
          </div>
        )}

        {activeTab === 'packing' && (
          <div style={{ overflowX: 'auto' }}>
            {renderPackingTable()}
          </div>
        )}

        {activeTab === 'build_up' && (
          <div style={{ overflowX: 'auto' }}>
            {renderBuildUpTable()}
          </div>
        )}

        {activeTab === 'storage' && (
          <div>
            {renderStorageSection()}
          </div>
        )}

        {activeTab === 'free_periods' && (
          <div>
            {renderFreePeriodSection()}
          </div>
        )}

        {activeTab === 'exchange_rate' && (
          <div className="card" style={{ maxWidth: '500px', margin: '20px auto', padding: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#1976d2' }}>Exchange Rate Management (USD / SDG)</h3>
            <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
              Set the exchange rate for Sudanese Pounds against USD (1 USD = X SDG) to persist dual-currency amounts and choose print currency for receipts and invoices.
            </p>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Exchange Rate (1 USD = SDG):</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                className="edit-input"
                style={{ width: '100%', fontSize: '16px', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                value={exchangeRate}
                onChange={(e) => setExchangeRate(e.target.value)}
              />
            </div>
            {rateMsg && <div style={{ marginBottom: '16px', fontWeight: 'bold' }}>{rateMsg}</div>}
            <button
              className="btn btn-primary"
              style={{ width: '100%', padding: '10px', fontSize: '16px' }}
              onClick={handleSaveExchangeRate}
              disabled={savingRate}
            >
              {savingRate ? 'Saving...' : 'Save Exchange Rate'}
            </button>
          </div>
        )}

        {activeTab === 'calc' && (
          <div className="calc-panel">
            <h3>حاسبة تكلفة الشحن</h3>
            <div className="calc-grid">
              <div className="calc-field">
                <label>نوع الخدمة</label>
                <select className="edit-select" style={{ width: '100%' }} value={calcType} onChange={(e) => setCalcType(e.target.value)}>
                  {cargoTypes.length > 0 ? (
                    cargoTypes.filter(ct => ct && ct.name).map(ct => (
                      <option key={ct.id} value={ct.id}>{ct.name}</option>
                    ))
                  ) : <option value="">لا توجد أنواع</option>}
                </select>
              </div>
              <div className="calc-field">
                <label>النوع (Type)</label>
                <select className="edit-select" style={{ width: '100%' }} value={calcSelectedType} onChange={(e) => setCalcSelectedType(e.target.value)}>
                  {services.length === 0 ? (
                    <option value="">لا توجد أنواع</option>
                  ) : (
                    [...new Set(services.map((s) => s.type))].map((type) => (
                      <option key={type} value={type}>
                        {type} ({getRateTypeForType(type)})
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="calc-field">
                <label>التصنيف المحدد تلقائياً</label>
                <div style={{ padding: '8px', background: '#f5f5f5', borderRadius: '4px', fontSize: '12px' }}>
                  {(() => {
                    const row = findRow(parseFloat(calcWeight) || 0, calcSelectedType);
                    return row ? formatScope(row) : 'لا يوجد تصنيف مناسب';
                  })()}
                </div>
              </div>
              <div className="calc-field">
                <label>الوزن (KG)</label>
                <input type="number" className="edit-input" style={{ width: '100%' }} value={calcWeight} min="1" onChange={(e) => setCalcWeight(e.target.value)} />
              </div>
              <div className="calc-field">
                <label>عدد AWB</label>
                <input type="number" className="edit-input" style={{ width: '100%' }} value={calcAwb} min="0" onChange={(e) => setCalcAwb(e.target.value)} />
              </div>
            </div>
            <div className="calc-result">
              <div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>التكلفة الإجمالية المقدّرة</div>
                <div className="calc-total" id="calc-out">{calcOut}</div>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', textAlign: 'right', lineHeight: '1.6' }} id="calc-breakdown">{calcBreakdown}</div>
            </div>
          </div>
        )}
      </div>

      <div className={`modal-overlay ${modalOpen ? 'open' : ''}`}>
        <div className="modal" style={{ overflowY: 'auto', maxHeight: '80vh' }}>
          <h3>{modalMode === 'edit' ? `Edit ${currentLabel}` : `Add ${currentLabel}`}</h3>

          {(modalKind === 'services') && (
            <>
              <div className="form-row">
                <label>Service Group</label>
                <input type="text" value={form.type} onChange={(e) => updateForm('type', e.target.value)} placeholder="Example: Normal, Minimum, Quantities" />
              </div>
              <div className="form-row">
                <label>Classification</label>
                <input type="text" value={form.classification} onChange={(e) => updateForm('classification', e.target.value)} placeholder="Example: Minimum / Per AWB / Per Ton" />
              </div>
              <div className="form-row-2">
                <div className="form-row">
                  <label>From</label>
                  <input type="number" value={form.rangeFrom} onChange={(e) => updateForm('rangeFrom', e.target.value)} placeholder="From" step="1" />
                </div>
                <div className="form-row">
                  <label>To</label>
                  <input type="number" value={form.rangeTo} onChange={(e) => updateForm('rangeTo', e.target.value)} placeholder="To" step="1" />
                </div>
              </div>
              <div className="form-row">
                <label>Rate Type</label>
                <select value={form.rateType} onChange={(e) => updateForm('rateType', e.target.value)}>
                  <option value="per_kg">Per KG</option>
                  <option value="per_ton">Per Ton</option>
                  <option value="per_awb">Per AWB</option>
                  <option value="free">Free</option>
                </select>
              </div>
              {cargoTypes.filter(ct => ct && ct.name).map(ct => (
                <div key={ct.id} className="form-row">
                  <label>Price for {ct.name} (USD)</label>
                  <input type="number" value={form.prices[ct.name.toLowerCase()] || ''} onChange={(e) => updateForm('prices', { ...form.prices, [ct.name.toLowerCase()]: e.target.value })} placeholder="0.00" step="0.01" />
                </div>
              ))}
              <div className="modal-actions">
                <button className="btn" onClick={closeModal}>Cancel</button>
                <button className="btn btn-primary" onClick={saveModal}>Save</button>
              </div>
            </>
          )}

          {(modalKind === 'packing') && (
            <>
              <div className="form-row">
                <label>Packing Type</label>
                <input type="text" value={form.type} onChange={(e) => updateForm('type', e.target.value)} placeholder="Example: Box, Pallet" />
              </div>
              <div className="form-row">
                <label>Classification</label>
                <input type="text" value={form.classification} onChange={(e) => updateForm('classification', e.target.value)} placeholder="Example: Fragile, Heavy" />
              </div>
              <div className="form-row">
                <label>Price (USD)</label>
                <input type="number" value={form.price} onChange={(e) => updateForm('price', e.target.value)} placeholder="0.00" step="0.01" />
              </div>
              <div className="modal-actions">
                <button className="btn" onClick={closeModal}>Cancel</button>
                <button className="btn btn-primary" onClick={saveModal}>Save</button>
              </div>
            </>
          )}

          {(modalKind === 'build_up') && (
            <>
              <div className="form-row">
                <label>Build Up Name</label>
                <input type="text" value={form.name} onChange={(e) => updateForm('name', e.target.value)} placeholder="Example: Build Up Fee" />
              </div>
              <div className="form-row">
                <label>Price (USD)</label>
                <input type="number" value={form.price} onChange={(e) => updateForm('price', e.target.value)} placeholder="0.00" step="0.01" />
              </div>
              <div className="modal-actions">
                <button className="btn" onClick={closeModal}>Cancel</button>
                <button className="btn btn-primary" onClick={saveModal}>Save</button>
              </div>
            </>
          )}

          {(modalKind === 'storage') && (
            <>
              <div className="form-row">
                <label>Cargo Type</label>
                <select value={form.type} onChange={(e) => updateForm('type', e.target.value)}>
                  <option value="">Select Type</option>
                  {cargoTypes.filter(ct => ct && ct.name).map(type => (
                    <option key={type.id} value={type.name}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Classification</label>
                <select value={form.classification} onChange={(e) => updateForm('classification', e.target.value)}>
                  <option value="">Select Classification</option>
                  <option value="Minimum">Minimum</option>
                  <option value="Normal">Normal</option>
                  <option value="Quantities">Quantities</option>
                </select>
              </div>
              <div className="form-row-2">
                <div className="form-row">
                  <label>From (kg)</label>
                  <input type="number" value={form.rangeFrom} onChange={(e) => updateForm('rangeFrom', e.target.value)} placeholder="1" step="1" />
                </div>
                <div className="form-row">
                  <label>To (kg)</label>
                  <input type="number" value={form.rangeTo} onChange={(e) => updateForm('rangeTo', e.target.value)} placeholder="100" step="1" />
                </div>
              </div>
              <div className="form-row">
                <label>Price (USD)</label>
                <input type="text" value={form.price} onChange={(e) => updateForm('price', e.target.value)} placeholder="0.00 or No need" />
              </div>
              <div className="form-row">
                <label>
                  <input type="checkbox" checked={form.isFixed} onChange={(e) => updateForm('isFixed', e.target.checked)} />
                  Fixed Price (not per kg)
                </label>
              </div>
              <div className="modal-actions">
                <button className="btn" onClick={closeModal}>Cancel</button>
                <button className="btn btn-primary" onClick={saveModal}>Save</button>
              </div>
            </>
          )}

          {(modalKind === 'cargo_types') && (
            <>
              <h4>Manage Cargo Types</h4>
              <div className="form-row">
                <input type="text" value={newCargoType} onChange={(e) => setNewCargoType(e.target.value)} placeholder="Enter new type" />
                <button className="btn btn-primary" onClick={addCargoType}>Add</button>
              </div>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {cargoTypes.filter(ct => ct && ct.name).map(ct => (
                  <li key={ct.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <span>{ct.name}</span>
                    <button className="btn btn-danger" onClick={() => deleteCargoType(ct.id, ct.name)}>Delete</button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {(modalKind === 'free_periods') && (
            <>
              <div className="form-row">
                <label>Cargo Type</label>
                <select value={form.type} onChange={(e) => updateForm('type', e.target.value)}>
                  <option value="">Select Type</option>
                  {cargoTypes.filter(ct => ct && ct.name).map(type => (
                    <option key={type.id} value={type.name}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Free Period</label>
                <input type="text" value={form.freePeriod} onChange={(e) => updateForm('freePeriod', e.target.value)} placeholder="Example: 24 hours, 04 hours, No need, None" />
              </div>
              <div className="modal-actions">
                <button className="btn" onClick={closeModal}>Cancel</button>
                <button className="btn btn-primary" onClick={saveModal}>Save</button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ServicesScreen;
